import { useMemo, useState } from "react";

export type SortDirection = "asc" | "desc";

/**
 * Minimal single-column client-side sort — toggle a column between
 * ascending/descending/unsorted and get back the re-ordered rows. Deliberately
 * headless (no table markup opinions) so it composes with any hand-rolled
 * `<table>`; pair with a header `<button>` that calls `toggle()` and renders
 * an icon based on `direction`.
 */
export function useSortableRows<T>(
  rows: T[],
  sortValue: (row: T) => string | number,
  initialDirection: SortDirection | null = null,
) {
  const [direction, setDirection] = useState<SortDirection | null>(
    initialDirection,
  );

  const sorted = useMemo(() => {
    if (!direction) return rows;
    const copy = [...rows];
    copy.sort((a, b) => {
      const av = sortValue(a);
      const bv = sortValue(b);
      if (av < bv) return direction === "asc" ? -1 : 1;
      if (av > bv) return direction === "asc" ? 1 : -1;
      return 0;
    });
    return copy;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows, direction]);

  const toggle = () => {
    setDirection((prev) => (prev === "desc" ? "asc" : "desc"));
  };

  return { sorted, direction, toggle };
}
